/**
 * MSSQL Script Provider
 *
 * Implements SQL script generation for Microsoft SQL Server.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
  DatabaseScriptContext,
  SchemaScriptType,
  SchemaScriptContext,
  ColumnMetadata,
  NewObjectTemplateContext,
} from '../types/scriptProvider';
import { defaultValuesInsert } from '../../../utils/defaultValuesInsert';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';
import { appendIndexStatements } from './indexStatements';
import { resolvePrimaryKey } from './primaryKeyClause';

/** SQL Server types whose only parameter is a fractional-seconds scale (0–7). */
const TEMPORAL_SCALED_TYPES = ['DATETIME2', 'TIME', 'DATETIMEOFFSET'];

/**
 * Dimension bounds for the SQL Server 2025 `vector` type: at least one
 * dimension, at most 1998 for the generally-available `float32` base type.
 */
const VECTOR_MIN_DIMENSIONS = 1;
const VECTOR_MAX_DIMENSIONS = 1998;
/** Fixed per-value overhead of a SQL Server vector, in bytes. */
const VECTOR_HEADER_BYTES = 8;
/** Width of one element of a default-base-type (float32) vector, in bytes. */
const VECTOR_FLOAT32_ELEMENT_BYTES = 4;

/**
 * Derive a VECTOR column's dimension count from the byte length SQL Server
 * reports in `sys.columns.max_length`.
 *
 * `max_length` is storage bytes, not dimensions: a float32 vector occupies an
 * 8-byte header plus 4 bytes per element, so a VECTOR(100) column reports 408.
 * Without this conversion an existing vector column would script as
 * VECTOR(408) — silently wrong, and within the valid range, so nothing later
 * would catch it.
 *
 * Returns null unless the derivation is exact and lands inside the supported
 * range. That guards half-precision (float16) vectors, whose elements are 2
 * bytes wide and whose true dimension count this formula cannot recover:
 * scripting a bare VECTOR the server rejects loudly beats emitting a
 * plausible-looking wrong one. (Recovering float16 dimensions properly needs
 * `sys.columns.vector_base_type`, which only exists on SQL Server 2025.)
 */
function deriveVectorDimensionsFromMaxLength(maxLength: number | null | undefined): number | null {
  if (typeof maxLength !== 'number' || !Number.isInteger(maxLength)) return null;
  const payloadBytes = maxLength - VECTOR_HEADER_BYTES;
  if (payloadBytes <= 0 || payloadBytes % VECTOR_FLOAT32_ELEMENT_BYTES !== 0) return null;
  const dimensions = payloadBytes / VECTOR_FLOAT32_ELEMENT_BYTES;
  return dimensions >= VECTOR_MIN_DIMENSIONS && dimensions <= VECTOR_MAX_DIMENSIONS
    ? dimensions
    : null;
}

/**
 * Map SQL Server data types to their script representation.
 *
 * Exported because the Object Explorer's user-defined-type generator
 * (`mssqlUserTypeScript.ts`) renders the columns of a `CREATE TYPE ... AS
 * TABLE` with exactly the same rules — most importantly the N-type byte ->
 * character halving of `max_length`, which is easy to get wrong twice.
 */
export function formatColumnDataType(col: ColumnMetadata): string {
  const { dataType, maxLength, precision, scale } = col;
  const upperType = dataType.toUpperCase();

  // SQL Server 2025 VECTOR(n): the dimension count is mandatory and has no MAX
  // form, so this is handled before the length types below (which fall back to
  // "(MAX)" on a missing value). The catalog reports storage bytes, not
  // dimensions — convert before emitting.
  if (upperType === 'VECTOR') {
    const dimensions = deriveVectorDimensionsFromMaxLength(maxLength);
    return dimensions != null ? `${upperType}(${dimensions})` : upperType;
  }

  // Types with length
  if (['VARCHAR', 'NVARCHAR', 'CHAR', 'NCHAR', 'BINARY', 'VARBINARY'].includes(upperType)) {
    if (maxLength === -1) {
      return `${upperType}(MAX)`;
    }
    // NVARCHAR/NCHAR store length in bytes, but we declare in characters
    const displayLength = upperType.startsWith('N') && maxLength ? maxLength / 2 : maxLength;
    return `${upperType}(${displayLength})`;
  }

  // Types with precision and scale
  if (['DECIMAL', 'NUMERIC'].includes(upperType)) {
    if (precision !== null && scale !== null) {
      return `${upperType}(${precision}, ${scale})`;
    }
    if (precision !== null) {
      return `${upperType}(${precision})`;
    }
    return upperType;
  }

  // Types with precision only (for float)
  if (upperType === 'FLOAT' && precision !== null && precision !== 53) {
    return `FLOAT(${precision})`;
  }

  // Temporal types with a fractional-seconds scale (1.4.21 review F2). The
  // catalog stores it in `sys.columns.scale`; the bare name re-creates at the
  // default of 7, so `datetime2(3)` used to script — and compare — as
  // `DATETIME2`. Emitted only when it differs from the default, so the
  // overwhelmingly common default keeps the same text it always had.
  if (TEMPORAL_SCALED_TYPES.includes(upperType) && scale != null && scale !== 7) {
    return `${upperType}(${scale})`;
  }

  // Other types use their name as-is
  return upperType;
}

/**
 * Generate CREATE TABLE script from metadata
 */
function generateCreateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `[${schema}].[${tableName}]`;

  if (!metadata?.columns?.length) {
    // No metadata - generate placeholder
    return `-- CREATE TABLE script for ${fullName}
-- Run TASK-045 to fetch table metadata for complete DDL generation
CREATE TABLE ${fullName} (
    -- Column definitions would appear here
);
`;
  }

  const lines: string[] = [];
  lines.push(`CREATE TABLE ${fullName} (`);

  // Column definitions
  const columnDefs: string[] = [];

  for (const col of metadata.columns) {
    let colDef = `    [${col.name}] ${formatColumnDataType(col)}`;

    if (col.isIdentity) {
      colDef += ' IDENTITY(1,1)';
    }

    colDef += col.isNullable ? ' NULL' : ' NOT NULL';

    if (col.defaultValue && !col.isIdentity) {
      colDef += ` DEFAULT ${col.defaultValue}`;
    }

    columnDefs.push(colDef);
  }

  lines.push(columnDefs.join(',\n'));

  // Primary key constraint — the catalog's name (the server-assigned
  // `PK__t__…` included), column order and index kind when the metadata
  // carries the constraint (see `resolvePrimaryKey`); CLUSTERED, SQL Server's
  // own default, only when the kind is unknown. A baseline that always said
  // CLUSTERED turned a NONCLUSTERED key into the table's clustered index on
  // the first key edit, since DROP + ADD restate the whole constraint.
  const primaryKey = resolvePrimaryKey(metadata, `PK_${tableName}`);
  if (primaryKey) {
    const pkCols = primaryKey.columns.map(c => `[${c}]`).join(', ');
    const kind = primaryKey.isClustered === false ? 'NONCLUSTERED' : 'CLUSTERED';
    lines.push(`,    CONSTRAINT ${quoteMssqlIdentifier(primaryKey.name)} PRIMARY KEY ${kind} (${pkCols})`);
  }

  lines.push(');');

  // The table's indexes follow as CREATE INDEX statements — the Designer's
  // Indexes tab reads them from the buffer (see `indexStatements.ts`).
  return appendIndexStatements(lines.join('\n'), metadata, {
    quote: quoteMssqlIdentifier, table: fullName, spellClustered: true,
  });
}

/**
 * Generate INSERT template script
 */
function generateInsertScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `[${schema}].[${tableName}]`;

  if (!metadata?.columns?.length) {
    return `INSERT INTO ${fullName} (column1, column2, ...)\nVALUES (value1, value2, ...);`;
  }

  // Filter out identity columns for INSERT
  const insertColumns = metadata.columns.filter(c => !c.isIdentity);
  // D5 — every column is IDENTITY: `INSERT INTO t () VALUES ();` is a syntax
  // error on SQL Server, so let the engine supply the whole row.
  if (insertColumns.length === 0) {
    return defaultValuesInsert({ engineId: 'mssql', tableName: fullName });
  }
  const columnNames = insertColumns.map(c => `[${c.name}]`).join(', ');
  const valuePlaceholders = insertColumns.map(c => {
    // Provide type-appropriate placeholders
    const upper = c.dataType.toUpperCase();
    if (['INT', 'BIGINT', 'SMALLINT', 'TINYINT', 'DECIMAL', 'NUMERIC', 'FLOAT', 'REAL', 'MONEY'].some(t => upper.includes(t))) {
      return '0';
    }
    if (upper.includes('BIT')) {
      return '0';
    }
    if (upper.includes('DATE') || upper.includes('TIME')) {
      return 'GETDATE()';
    }
    return `N''`;
  }).join(', ');

  return `INSERT INTO ${fullName} (${columnNames})\nVALUES (${valuePlaceholders});`;
}

/**
 * Generate UPDATE template script
 */
function generateUpdateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `[${schema}].[${tableName}]`;

  if (!metadata?.columns?.length) {
    return `UPDATE ${fullName}\nSET column1 = value1,\n    column2 = value2\nWHERE <search_condition>;`;
  }

  // Find primary key columns for WHERE clause
  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const updateColumns = metadata.columns.filter(c => !c.isPrimaryKey && !c.isIdentity);

  const setClauses = updateColumns.slice(0, 3).map(c => `    [${c.name}] = <value>`).join(',\n');
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `[${c.name}] = <value>`).join(' AND ')
    : '<search_condition>';

  return `UPDATE ${fullName}\nSET\n${setClauses}\nWHERE ${whereClause};`;
}

/**
 * Generate DELETE template script
 */
function generateDeleteScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `[${schema}].[${tableName}]`;

  if (!metadata?.columns?.length) {
    return `DELETE FROM ${fullName}\nWHERE <search_condition>;`;
  }

  // Find primary key columns for WHERE clause
  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `[${c.name}] = <value>`).join(' AND ')
    : '<search_condition>';

  return `DELETE FROM ${fullName}\nWHERE ${whereClause};`;
}

/** `[name]`, with an embedded `]` doubled — the only character a bracketed
 *  identifier escapes. Left unescaped, a constraint named `PK]x` was dropped as
 *  `DROP CONSTRAINT [PK]x]` (Msg 102). */
function quoteMssqlIdentifier(name: string): string {
  return `[${name.replace(/]/g, ']]')}]`;
}

export const mssqlScriptProvider: ScriptProvider = {
  engine: 'mssql',

  quoteIdentifier(name: string): string {
    return quoteMssqlIdentifier(name);
  },

  qualifyTableName(schema: string, table: string): string {
    return `[${schema}].[${table}]`;
  },

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    const fullName = this.qualifyTableName(ctx.schema, ctx.tableName);

    switch (type) {
      case 'create':
        return generateCreateScript(ctx);

      case 'drop':
        return `DROP TABLE IF EXISTS ${fullName};`;

      case 'select':
        return `SELECT *\nFROM ${fullName};`;

      case 'select-top':
        return `SELECT TOP ${SELECT_TOP_ROW_LIMIT} *\nFROM ${fullName};`;

      case 'insert':
        return generateInsertScript(ctx);

      case 'update':
        return generateUpdateScript(ctx);

      case 'delete':
        return generateDeleteScript(ctx);

      default:
        throw new Error(`Unknown table script type: ${type}`);
    }
  },

  generateDatabaseScript(type: DatabaseScriptType, ctx: DatabaseScriptContext): string {
    const { databaseName, metadata } = ctx;
    const quotedName = `[${databaseName}]`;

    switch (type) {
      case 'create':
        if (metadata) {
          const collation = metadata.collation ? `\n    COLLATE ${metadata.collation}` : '';
          return `CREATE DATABASE ${quotedName}${collation};\nGO`;
        }
        return `CREATE DATABASE ${quotedName};\nGO`;

      case 'drop':
        return `-- Warning: This will permanently delete the database and all its data\nUSE [master];\nGO\n\nALTER DATABASE ${quotedName} SET SINGLE_USER WITH ROLLBACK IMMEDIATE;\nGO\n\nDROP DATABASE IF EXISTS ${quotedName};\nGO`;

      case 'use':
        return `USE ${quotedName};\nGO`;

      default:
        throw new Error(`Unknown database script type: ${type}`);
    }
  },

  generateSchemaScript(type: SchemaScriptType, ctx: SchemaScriptContext): string {
    const quotedSchema = `[${ctx.schema}]`;
    switch (type) {
      case 'create':
        return `CREATE SCHEMA ${quotedSchema};\nGO`;
      case 'drop':
        return `DROP SCHEMA ${quotedSchema};\nGO`;
      default:
        throw new Error(`Unknown schema script type: ${type}`);
    }
  },

  // ============================================
  // "Script New" Templates with VS Code tabstops
  // ============================================

  generateNewTableTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new table in [${database}].[${schema}]
CREATE TABLE [${schema}].[\${1:TableName}] (
    [\${2:Id}] INT IDENTITY(1,1) NOT NULL,
    [\${3:Column1}] \${4:NVARCHAR(255)} \${5|NULL,NOT NULL|},
    [CreatedAt] DATETIME2 NOT NULL DEFAULT GETDATE(),
    CONSTRAINT [PK_\${1:TableName}] PRIMARY KEY CLUSTERED ([\${2:Id}])
);
GO\$0`;
  },

  generateNewViewTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new view in [${database}].[${schema}]
CREATE VIEW [${schema}].[\${1:ViewName}]
AS
SELECT
    \${2:*}
FROM
    [${schema}].[\${3:SourceTable}]
WHERE
    \${4:1=1};
GO\$0`;
  },

  generateNewProcedureTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new stored procedure in [${database}].[${schema}]
CREATE PROCEDURE [${schema}].[\${1:ProcedureName}]
    @\${2:Parameter1} \${3:INT},
    @\${4:Parameter2} \${5:NVARCHAR(255)} = NULL
AS
BEGIN
    SET NOCOUNT ON;

    \${6:-- Procedure logic here}

END;
GO\$0`;
  },

  generateNewFunctionTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new scalar function in [${database}].[${schema}]
CREATE FUNCTION [${schema}].[\${1:FunctionName}]
(
    @\${2:Parameter1} \${3:INT}
)
RETURNS \${4:INT}
AS
BEGIN
    DECLARE @Result \${4:INT};

    \${5:-- Function logic here}

    RETURN @Result;
END;
GO\$0`;
  },

  generateNewDatabaseTemplate(): string {
    return `-- Create new database
CREATE DATABASE [\${1:DatabaseName}]
    COLLATE \${2:SQL_Latin1_General_CP1_CI_AS};
GO

USE [\${1:DatabaseName}];
GO\$0`;
  },
};
